import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { invoke } from '@tauri-apps/api/core';
import { Modal } from './ui/Modal';
import VaultPassphraseInput from './VaultPassphraseInput';
import {
  RECOVERY_WORD_COUNT,
  normalizePhrase,
  phraseWords,
  unknownRecoveryWords,
} from './vaultPassphraseUtils';
import {
  UNLOCK_PASSPHRASE_ERROR,
  UNLOCK_RECOVERY_ERROR,
  UNLOCK_RETRY_DELAY_MS,
} from './vaultPassphraseCopy';
import { refreshVaultMaster, vaultErrorToken } from '../store/vaultStore';
import './Vault.css';

/**
 * The unlock gate — CONTRACT §6.3.
 *
 * **Two panes, one at a time.** Passphrase and recovery phrase never share a
 * screen: a form with both fields invites typing the phrase into the passphrase
 * box, and the two want opposite input affordances.
 *
 * **No-oracle rules, all of them load-bearing:**
 * - one fixed string per pane whatever Rust returned (`bad_passphrase`,
 *   `vault_kdf_corrupt`, "no vault") — the constants live in
 *   `vaultPassphraseCopy.js` so the two call sites cannot drift;
 * - no attempt counter, no lockout, no progressive delay beyond a flat
 *   one-second submit disable that is never persisted. A lockout on a local
 *   vault only ever denies the owner;
 * - the KDF never runs on a keystroke — submit only;
 * - **no destructive control on this screen** (H5). Someone who has just failed
 *   three times must not be one click from "delete this vault and start over".
 *
 * The unknown-word hint on pane B is not an oracle either: the BIP39 list is
 * public and already ships in the binary. It turns an unrecoverable-feeling
 * failure into a typo fix, and it never blocks submission. Nothing here checks
 * distinctness or a checksum — a recovery phrase has neither.
 */
export default function VaultPassphraseUnlockModal({
  isOpen,
  onClose,
  onUnlocked,
  /** Free-form tag from `vault-gate-open` — picks the subtitle only. */
  reason = 'vault',
}) {
  const { t } = useTranslation();
  const [pane, setPane] = useState('passphrase');
  const [passphrase, setPassphrase] = useState('');
  const [phrase, setPhrase] = useState('');
  const [busy, setBusy] = useState(false);
  const [cooling, setCooling] = useState(false);
  const [error, setError] = useState('');
  const coolTimer = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    setPane('passphrase');
    setPassphrase('');
    setPhrase('');
    setBusy(false);
    setCooling(false);
    setError('');
  }, [isOpen]);

  useEffect(
    () => () => {
      if (coolTimer.current) clearTimeout(coolTimer.current);
    },
    []
  );

  const cool = useCallback(() => {
    setCooling(true);
    if (coolTimer.current) clearTimeout(coolTimer.current);
    coolTimer.current = setTimeout(() => setCooling(false), UNLOCK_RETRY_DELAY_MS);
  }, []);

  const submit = async (secret, failureText) => {
    if (busy || cooling || !secret) return;
    setBusy(true);
    setError('');
    try {
      // The OS-keychain device cache is not offered (`device_cache_supported`),
      // so there is nothing to opt into and no checkbox to get wrong. Rust
      // ignores this, but sending `false` keeps the wire honest.
      await invoke('vault_unlock_with_secret', { secret, remember: false });
      setPassphrase('');
      setPhrase('');
      await refreshVaultMaster();
      if (onUnlocked) onUnlocked();
    } catch (e) {
      // Otherwise deliberately ignores `e`. Which token came back — a wrong
      // secret, a failed GCM tag, an absent vault — is exactly what must not
      // reach this screen. The raw error is already in Rust's log.
      // The one exception: the secret DID open the passphrase set on another
      // device, but this computer must first prove it descends from its own
      // key (code review of #475). Only someone who knows that new passphrase
      // ever sees this, so it reveals nothing.
      if (vaultErrorToken(e) === 'vault_rekey_unlock_old_first') {
        setError(
          t(
            'vault.rekeyUnlockOldFirst',
            'That passphrase was set on another device. Unlock with your previous passphrase first; you will then be asked for the new one.'
          )
        );
      } else {
        setError(failureText);
      }
      cool();
    }
    setBusy(false);
  };

  // Both fixed strings go through a translation key even though the constant is
  // right here: the change screen renders the same sentence, and routing both
  // through one key is what stops a locale entry added later from making them
  // differ — which would turn "wrong current passphrase" into an oracle.
  const passphraseFailed = t('vault.unlockFailed', UNLOCK_PASSPHRASE_ERROR);
  const recoveryFailed = t('vault.recoveryFailed', UNLOCK_RECOVERY_ERROR);

  const words = phraseWords(phrase);
  const unknown = unknownRecoveryWords(phrase);

  const handleClose = () => {
    if (busy) return;
    setPassphrase('');
    setPhrase('');
    if (onClose) onClose();
  };

  const subtitleFor = () => {
    switch (reason) {
      case 'ftp':
        return t('vault.gateSubtitleFtp', 'Unlock your vault to save this connection.');
      case 'chat':
        return t('vault.gateSubtitleChat', 'Unlock your vault to read this secret.');
      // Git tokens and extension secrets (they used to sit in the OS keychain).
      case 'git-account':
      case 'git-sync':
      case 'secure-storage':
        return t('vault.gateSubtitleSecret', 'Unlock your vault to use this saved sign-in.');
      default:
        return t('vault.unlockSubtitle', 'Enter your passphrase to open it.');
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      preventBackdropClose
      size="xl"
      // Above the popovers (10040, e.g. the terminal "+" / "Split with…" menus)
      // and above the modals (z-50, e.g. the SSH connection picker) that open
      // this gate with their Unlock button.
      zIndex={10050}
      title={
        pane === 'recovery'
          ? t('vault.recoveryPaneTitle', 'Use your recovery phrase')
          : t('vault.unlockTitle', 'Vault locked')
      }
      subtitle={pane === 'recovery' ? '' : subtitleFor()}
    >
      <div className="vault-pass-body">
        {pane === 'passphrase' ? (
          <>
            <VaultPassphraseInput
              id="vault-passphrase-unlock"
              label={t('vault.unlockField', 'Passphrase')}
              value={passphrase}
              onChange={setPassphrase}
              autoComplete="current-password"
              autoFocus
              disabled={busy}
              onKeyDown={(e) => {
                if (e.key === 'Enter') submit(passphrase, passphraseFailed);
              }}
            />
            {error && <div className="vault-modal-error">{error}</div>}
            <button
              type="button"
              className="vault-pass-linkbtn"
              onClick={() => {
                setPane('recovery');
                setError('');
              }}
            >
              {t('vault.unlockForgot', 'Forgot your passphrase? Use your recovery phrase')}
            </button>
          </>
        ) : (
          <>
            <p className="vault-pass-text">
              {t(
                'vault.recoveryPaneBody',
                'Type the 12 words in the order they were given. Capitals and extra spaces do not matter.'
              )}
            </p>
            <textarea
              className="vault-pass-recovery-input"
              rows={3}
              value={phrase}
              onChange={(e) => setPhrase(e.target.value)}
              placeholder={t('vault.recoveryPlaceholder', 'twelve words separated by spaces')}
              aria-label={t('vault.recoveryPaneTitle', 'Use your recovery phrase')}
              spellCheck={false}
              autoCapitalize="none"
              autoCorrect="off"
              autoComplete="off"
              disabled={busy}
            />
            <span className="vault-pass-hint">
              {t('vault.recoveryCounter', '{{n}} of {{total}} words', {
                n: words.length,
                total: RECOVERY_WORD_COUNT,
              })}
            </span>
            {unknown.length > 0 && (
              <span className="vault-pass-hint">
                {t(
                  'vault.recoveryUnknownWords',
                  'Not on the recovery word list: {{words}}. Check the spelling.',
                  { words: unknown.join(', ') }
                )}
              </span>
            )}
            {error && <div className="vault-modal-error">{error}</div>}
            <button
              type="button"
              className="vault-pass-linkbtn"
              onClick={() => {
                setPane('passphrase');
                setError('');
              }}
            >
              {t('vault.recoveryUsePassphrase', 'Use your passphrase instead')}
            </button>
          </>
        )}

        <div className="modal-footer-row">
          <button
            type="button"
            onClick={handleClose}
            disabled={busy}
            className="px-3 py-1.5 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors modern-button-secondary text-sm"
          >
            {t('common.cancel')}
          </button>
          {pane === 'passphrase' ? (
            <button
              type="button"
              onClick={() => submit(passphrase, passphraseFailed)}
              disabled={busy || cooling || !passphrase}
              aria-busy={busy}
              aria-label={busy ? t('vault.unlocking', 'Unlocking…') : undefined}
              style={{ minWidth: 78 }}
              className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors modern-button text-sm inline-flex items-center justify-center"
            >
              {busy ? (
                // The environments-save spinner, reused verbatim: while the
                // KDF runs (seconds on a slow machine) a spinner reads as
                // "working" where a static word reads as a frozen button.
                // The label moves to aria-label so the button keeps announcing
                // its state to a screen reader once the text is gone.
                <span className="env-save-spinner" />
              ) : (
                t('vault.unlock', 'Unlock')
              )}
            </button>
          ) : (
            <button
              type="button"
              // `normalizePhrase` here, not the raw textarea value: Rust
              // normalises the same way, so sending the tidied form keeps the
              // two sides identical and makes a stray newline a non-event.
              onClick={() => submit(normalizePhrase(phrase), recoveryFailed)}
              disabled={busy || cooling || words.length === 0}
              aria-busy={busy}
              aria-label={busy ? t('vault.unlocking', 'Unlocking…') : undefined}
              style={{ minWidth: 78 }}
              className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors modern-button text-sm inline-flex items-center justify-center"
            >
              {busy ? (
                // The environments-save spinner, reused verbatim: while the
                // KDF runs (seconds on a slow machine) a spinner reads as
                // "working" where a static word reads as a frozen button.
                // The label moves to aria-label so the button keeps announcing
                // its state to a screen reader once the text is gone.
                <span className="env-save-spinner" />
              ) : (
                t('vault.unlock', 'Unlock')
              )}
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
}
